import React, { useEffect, useRef, useState } from "react";
import { api, inr, grams } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Radio, MagnifyingGlass, ClipboardText, X, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function RFID() {
  const [lookupRfid, setLookupRfid] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [scanInput, setScanInput] = useState("");
  const [scannedList, setScannedList] = useState([]);
  const [audit, setAudit] = useState(null);
  const scanRef = useRef(null);

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)); }, []);

  const doLookup = async () => {
    if (!lookupRfid.trim()) return;
    try {
      const { data } = await api.get(`/rfid/lookup?rfid=${encodeURIComponent(lookupRfid.trim())}`);
      setLookupResult(data);
    } catch (e) {
      setLookupResult(null);
      toast.error(e.response?.data?.detail || "Not found");
    }
  };

  const addScan = (val) => {
    const v = (val || scanInput).trim();
    if (!v) return;
    if (scannedList.includes(v)) { toast.message("Already scanned"); }
    else setScannedList((arr) => [...arr, v]);
    setScanInput("");
    scanRef.current?.focus();
  };

  const removeScan = (i) => setScannedList((arr) => arr.filter((_, idx) => idx !== i));

  const runAudit = async () => {
    if (scannedList.length === 0) { toast.error("Scan at least one RFID"); return; }
    try {
      const payload = { scanned_rfids: scannedList };
      if (branchId) payload.branch_id = branchId;
      const { data } = await api.post("/rfid/audit", payload);
      setAudit(data);
      toast.success(`Audit complete · ${data.match_percent}% match`);
    } catch (e) { toast.error(e.response?.data?.detail || "Audit failed"); }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="rfid-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Tag Operations</div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">RFID</h1>
        <p className="text-muted-foreground text-sm mt-1">Look up a tag instantly or run a full inventory audit against scanned tags.</p>
      </header>

      {/* Lookup */}
      <section className="rounded-xl border border-border bg-card/40 p-6 space-y-4">
        <h2 className="font-display text-xl flex items-center gap-2"><Radio size={18} weight="duotone" className="text-gold" /> Tag lookup</h2>
        <div className="flex flex-wrap gap-2 max-w-2xl">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="rfid-lookup-input"
              value={lookupRfid}
              onChange={(e) => setLookupRfid(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doLookup()}
              placeholder="Scan or paste RFID..."
              className="pl-9 bg-background border-border"
            />
          </div>
          <Button data-testid="rfid-lookup-btn" onClick={doLookup} className="bg-primary text-primary-foreground hover:bg-primary/90">Lookup</Button>
        </div>
        {lookupResult && (
          <div data-testid="rfid-lookup-result" className="rounded-lg border border-gold/40 bg-gold/5 p-4">
            <div className="flex items-start gap-4">
              <div className="h-16 w-16 rounded-md bg-secondary border border-border overflow-hidden grid place-items-center shrink-0">
                {lookupResult.image_url ? <img src={lookupResult.image_url} alt="" className="h-full w-full object-cover" /> : <Radio size={20} className="text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg">{lookupResult.name}</div>
                <div className="text-xs text-muted-foreground font-mono">{lookupResult.product_code} · RFID {lookupResult.rfid}</div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                  <span>Net wt: <b className="tabular-nums">{grams(lookupResult.net_weight)}</b></span>
                  <span>Qty: <b className="tabular-nums">{lookupResult.quantity}</b></span>
                  <span>Price: <b className="tabular-nums text-gold">{inr(lookupResult.sale_price)}</b></span>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Audit */}
      <section className="rounded-xl border border-border bg-card/40 p-6 space-y-4">
        <h2 className="font-display text-xl flex items-center gap-2"><ClipboardText size={18} weight="duotone" className="text-gold" /> Stock audit</h2>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="min-w-[200px]">
            <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Branch (optional)</Label>
            <Select value={branchId || "_all_"} onValueChange={(v) => setBranchId(v === "_all_" ? "" : v)}>
              <SelectTrigger data-testid="rfid-branch-select" className="mt-1.5 bg-background border-border"><SelectValue placeholder="All branches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all_">All branches</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="relative flex-1 min-w-[220px]">
            <Radio size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={scanRef}
              data-testid="rfid-scan-input"
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addScan()}
              placeholder="Scan tag and press Enter..."
              autoFocus
              className="pl-9 bg-background border-border"
            />
          </div>
          <Button data-testid="rfid-add-scan" onClick={() => addScan()} variant="ghost" className="text-gold">Add</Button>
          <Button data-testid="rfid-audit-btn" onClick={runAudit} className="bg-primary text-primary-foreground hover:bg-primary/90">Run audit</Button>
        </div>

        {scannedList.length > 0 && (
          <div className="rounded-lg border border-border p-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">{scannedList.length} scanned</div>
            <div className="flex flex-wrap gap-2">
              {scannedList.map((s, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded-md bg-secondary border border-border flex items-center gap-1.5">
                  <span className="font-mono">{s}</span>
                  <button onClick={() => removeScan(i)} className="text-muted-foreground hover:text-destructive"><X size={10} /></button>
                </span>
              ))}
            </div>
          </div>
        )}

        {audit && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Expected" value={audit.expected_count} />
              <Stat label="Scanned" value={audit.scanned_count} />
              <Stat label="Match" value={`${audit.match_percent}%`} accent />
              <Stat label="Missing" value={audit.missing.length} bad />
            </div>
            <AuditList title="Found" items={audit.found} icon={CheckCircle} color="text-green-400" />
            <AuditList title="Missing (in stock but not scanned)" items={audit.missing} icon={Warning} color="text-rose-400" />
            {audit.extra.length > 0 && (
              <div className="rounded-lg border border-border p-3">
                <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5"><Warning size={12} className="text-orange-400" /> Extra (scanned but unknown):</div>
                <div className="flex flex-wrap gap-2 font-mono text-xs">{audit.extra.map((e) => <span key={e} className="px-2 py-1 rounded bg-secondary border border-border">{e}</span>)}</div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

const Stat = ({ label, value, accent, bad }) => (
  <div className={`rounded-lg border p-3 ${accent ? "border-gold/40 bg-gold/5" : bad ? "border-rose-500/30 bg-rose-500/5" : "border-border"}`}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
    <div className={`font-display text-xl mt-1 tabular-nums ${accent ? "text-gold" : bad ? "text-rose-400" : ""}`}>{value}</div>
  </div>
);

const AuditList = ({ title, items, icon: Icon, color }) => (
  <div className="rounded-lg border border-border overflow-hidden">
    <div className="px-3 py-2 bg-secondary/40 text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1.5">
      <Icon size={12} className={color} /> {title} ({items.length})
    </div>
    <Table>
      <TableHeader><TableRow className="bg-secondary/20 hover:bg-secondary/20">
        <TableHead>RFID</TableHead><TableHead>Code</TableHead><TableHead>Name</TableHead>
        <TableHead className="text-right">Wt</TableHead><TableHead className="text-right">Price</TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {items.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-4 text-muted-foreground text-sm">None</TableCell></TableRow> :
          items.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="font-mono text-xs">{p.rfid}</TableCell>
              <TableCell className="font-mono text-xs">{p.product_code}</TableCell>
              <TableCell className="text-sm">{p.name}</TableCell>
              <TableCell className="text-right tabular-nums">{grams(p.net_weight)}</TableCell>
              <TableCell className="text-right tabular-nums">{inr(p.sale_price)}</TableCell>
            </TableRow>
          ))
        }
      </TableBody>
    </Table>
  </div>
);
